import { Accessibility, DirectionsRun, FavoriteBorder, FitnessCenter, Groups, Healing, LocalHospital, MonitorHeart, People, Psychology, Schedule, Security, SelfImprovement, Spa, Star, SupportAgent, Verified } from '@mui/icons-material';
import { SxProps, SvgIconProps, Theme } from '@mui/material';
import { JSX } from 'react';

/**
 * The icons a clinic may put on a card it has written.
 *
 * A fixed set rather than a free choice, and this is the whole of it. An admin picks from
 * these; the name is what gets stored, and the API takes it as text without checking it
 * against anything -- which is the point, since which icons exist is a fact about this file
 * and not about the database.
 *
 * The set is deliberately small and thematically narrow. A page's cards have a look, and a
 * picker offering the whole Material set is how that look stops being one.
 *
 * <h2>Why it is here and not in a page folder</h2>
 *
 * It began as `About/profileIcons`, which was right while the About page was the only surface
 * a clinic wrote cards for. The front page is the second, its editor needs the same picker,
 * and a clinic that has learned what HEALING means on one page should not find a different
 * sixteen on the other. So the set moved here and the page-level modules bind their own
 * sizing to it -- the styling is what genuinely differs between the two, not the vocabulary.
 */
const ICONS: Record<string, (props: SvgIconProps) => JSX.Element> = {
    HEALING: props => <Healing {...props} />,
    PEOPLE: props => <People {...props} />,
    SCHEDULE: props => <Schedule {...props} />,
    STAR: props => <Star {...props} />,
    FAVORITE: props => <FavoriteBorder {...props} />,
    VERIFIED: props => <Verified {...props} />,
    // Added with the front page, whose shipped "secure and private" card has always drawn it.
    SECURITY: props => <Security {...props} />,
    ACCESSIBILITY: props => <Accessibility {...props} />,
    FITNESS_CENTER: props => <FitnessCenter {...props} />,
    LOCAL_HOSPITAL: props => <LocalHospital {...props} />,
    MONITOR_HEART: props => <MonitorHeart {...props} />,
    PSYCHOLOGY: props => <Psychology {...props} />,
    SELF_IMPROVEMENT: props => <SelfImprovement {...props} />,
    SPA: props => <Spa {...props} />,
    GROUPS: props => <Groups {...props} />,
    DIRECTIONS_RUN: props => <DirectionsRun {...props} />,
    SUPPORT_AGENT: props => <SupportAgent {...props} />,
};

/** What an unrecognised name, or no name at all, draws. */
export const DEFAULT_CONTENT_ICON: string = 'HEALING';

/** Every picker's contents, in the order it draws them. */
export const CONTENT_ICON_NAMES: string[] = Object.keys(ICONS);

/**
 * The icon for a stored name, at whatever size the caller draws one.
 *
 * An unknown name falls back to the default rather than rendering nothing. That is not
 * defensive padding: the API stores whatever name it is given, so a card written by a newer
 * build of this app -- or by hand -- can name an icon this build has never heard of, and a
 * card that silently loses its tile is worse than one wearing the wrong symbol.
 */
export const contentIcon = (name: string | null | undefined, sx?: SxProps<Theme>): JSX.Element =>
    resolve(name)({ sx });

/** The same icon at the size an editor's picker draws its tiles. */
export const contentIconTile = (name: string): JSX.Element =>
    resolve(name)({ fontSize: 'small' });

const resolve = (name: string | null | undefined): (props: SvgIconProps) => JSX.Element =>
    (name && ICONS[name]) || ICONS[DEFAULT_CONTENT_ICON];
